import React, { useState } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import MenuItem from '@material-ui/core/MenuItem';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import { deleteSurvey } from '../actions/survey/delete-survey';
import { endSurvey } from '../actions/survey/end-survey';

import { UiButton } from '../../ui/ui-button';
import { UiKebabMenu } from '../../ui/ui-kebab-menu';
import { surveysRoute } from '../../web-routes';

import { SurveyResponseProgressBar } from './survey-response-progress-bar';

import { surveyRecipients } from './utils/survey-recipients';
import { formatDateTime, formatRelativeDate } from '../../util/formatters';
import { HTMLStripper } from '../../util/html-stripper';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { confirm } from '../../widgets/confirmation-dialog';

type InputProps = {
  orgId: number;
  surveys: Array<Survey>;
  isCurrentlyRunning?: boolean;
};

const DEFAULT_PAGE_SIZE = 5;

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    endSurvey: (surveyId: number) => dispatch(endSurvey(surveyId)),
    deleteSurvey: (surveyId: number) => dispatch(deleteSurvey(surveyId)),
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const EmployeeSurveyTable = connect(
  null,
  mapDispatchToProps,
)(({ orgId, surveys, isCurrentlyRunning, actions }: Props) => {
  const [offset, setOffset] = useState<number>(0);
  const [pageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const handleDeleteSurvey = (survey: Survey) => () => {
    actions.deleteSurvey(survey.surveyId);
  };

  const handleEndSurveyClick = (surveyToEndId: number) => async () => {
    if (await confirm('Ending a survey prevents future responses.')) {
      actions.endSurvey(surveyToEndId);
    }
  };

  const changePage = (_event: any, page: number) => {
    setOffset(page * pageSize);
  };

  return (
    <>
      <Table className="u-margin-bottom-md">
        <TableHead>
          <TableRow>
            <TableCell>Sent On</TableCell>
            <TableCell>Sent To</TableCell>
            <TableCell>Anonymous</TableCell>
            <TableCell>Survey Question</TableCell>
            {isCurrentlyRunning && <TableCell>Progress</TableCell>}
            <TableCell />
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {surveys.slice(offset, offset + pageSize).map((row) => {
            const cleanMessage = HTMLStripper.strip(row.messageBody) || '--';
            return (
              <TableRow key={row.surveyId}>
                <TableCell size="small" style={{ width: '15%' }}>
                  {formatDateTime(row.sentAt)} <br />
                  {moment(row.endAt).isAfter(moment.utc())
                    ? `Ends ${formatRelativeDate(row.endAt)}`
                    : null}
                </TableCell>
                <TableCell size="small" style={{ width: '10%' }}>
                  {surveyRecipients(row)}
                </TableCell>
                <TableCell size="small" style={{ textAlign: 'center' }}>
                  {row.isAnonymous && <MaterialDesignIcon iconName={ICON_NAMES.CHECK} />}
                </TableCell>
                <TableCell size="small" style={{ width: isCurrentlyRunning ? '30%' : '55%' }}>
                  {cleanMessage.length > 200 ? `${cleanMessage.slice(0, 200)}...` : cleanMessage}
                </TableCell>
                {isCurrentlyRunning && (
                  <TableCell style={{ width: '25%' }}>
                    <SurveyResponseProgressBar surveyId={row.surveyId} />
                  </TableCell>
                )}
                <TableCell size="small" style={{ width: '30%', textAlign: 'right' }}>
                  <div className={isCurrentlyRunning ? 'u-spread' : ''}>
                    <Link to={surveysRoute(orgId, row.surveyId)}>
                      <UiButton
                        variant={isCurrentlyRunning ? 'primary' : 'secondary'}
                        dataCy={`view-${isCurrentlyRunning ? 'active-' : ''}survey-results`}
                      >
                        View Results
                      </UiButton>
                    </Link>
                    {isCurrentlyRunning ? (
                      <UiButton
                        className="u-margin-left-sm"
                        variant="secondary"
                        onClick={handleEndSurveyClick(row.surveyId)}
                        dataCy="end-survey"
                      >
                        End Survey
                      </UiButton>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell size="small" style={{ width: '1%', padding: '0px' }}>
                  {!isCurrentlyRunning ? (
                    <UiKebabMenu menuId={`delete-survey-${row.surveyId}`}>
                      <MenuItem onClick={handleDeleteSurvey(row)}>Delete</MenuItem>
                    </UiKebabMenu>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {surveys.length >= pageSize && (
        <TablePagination
          component="div"
          count={surveys.length}
          rowsPerPage={pageSize}
          page={offset / pageSize}
          backIconButtonProps={{ 'aria-label': 'Previous Page' }}
          nextIconButtonProps={{ 'aria-label': 'Next Page' }}
          onPageChange={changePage}
          rowsPerPageOptions={[pageSize]}
        />
      )}
    </>
  );
});
